/**
 * Production on-device WEB speech-to-text — BATCH engine.
 *
 * Recognition runs fully in the browser via @huggingface/transformers v4 (Whisper),
 * so audio never leaves the device. This module owns the MAIN-thread half of a
 * batch pipeline (one clean decode per utterance — no live word-by-word; the
 * level meter covers "you're being heard" feedback, and native platforms get live
 * interims from the system recogniser):
 *
 *   mic (warm MediaStream)
 *     → AudioWorklet (public/voice/audio-capture-worklet.js): raw PCM off-thread
 *     → main thread relays PCM chunks (+ RMS for the level meter)
 *     → STT worker (public/voice/stt-worker.js): accumulate → energy VAD → decode
 *        the WHOLE utterance ONCE on end-of-utterance (WASM)
 *     → onFinal + onEnd (auto-send) / onLevel (meter)
 *
 * ENV PARITY: lib + ONNX wasm + weights are ALL self-hosted under one base URL
 * (`EXPO_PUBLIC_VOICE_STT_ASSET_BASE_URL`, default same-origin `/voice-stt`;
 * `npm run provision:voice-stt`) — never a public CDN. Gated by the voice feature
 * flag + browser capability; any failure fails closed to text.
 *
 * ⚠️ UNVERIFIED in a browser here — needs an on-device test pass (and likely some
 * VAD threshold tuning).
 */
import { VOICE_ENABLED } from "./flag";
import type { SttHandle, SttHandlers } from "./stt-types";

/** Self-hosted asset base (per-env variable; default same-origin). No CDN. */
const ASSET_BASE: string = (
  process.env.EXPO_PUBLIC_VOICE_STT_ASSET_BASE_URL ??
  (typeof window !== "undefined" ? `${window.location.origin}/voice-stt` : "")
).replace(/\/$/, "");

/**
 * Committed static assets (served, not provisioned/git-ignored). The ?v cache-
 * buster forces the browser to pick up a new worker/worklet build — a cached
 * module Worker is NOT replaced by a normal reload. Bump on worker/worklet edits.
 */
// 12: the worker learned to report model-download progress. (11 was the
// cache-bust past the pre-wasm-unsafe-eval responses; /voice/* is no-cache
// now, so bumps are belt, not load-bearing.)
const ASSET_V = "12";
const WORKER_URL = `/voice/stt-worker.js?v=${ASSET_V}`;
const WORKLET_URL = `/voice/audio-capture-worklet.js?v=${ASSET_V}`;

export const LOCAL_WEB_STT_LABEL = "on-device (local model)";

/**
 * Sync capability gate. "Available" means the browser primitives exist, an asset
 * base is configured, and the voice feature is enabled. Weights load lazily in
 * the worker on first use.
 */
export const LOCAL_WEB_STT_AVAILABLE: boolean =
  VOICE_ENABLED &&
  ASSET_BASE.length > 0 &&
  typeof Worker !== "undefined" &&
  typeof WebAssembly !== "undefined" &&
  typeof navigator !== "undefined" &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  typeof (globalThis as { AudioContext?: unknown }).AudioContext !== "undefined";

/**
 * Must match `MODEL` in public/voice/stt-worker.js — the probe below asks for
 * the first file the worker's pipeline will fetch, so a rename that reaches one
 * file and not the other makes the probe vouch for assets the worker never
 * loads. tests/mobile/voice-asset-probe.test.ts pins the two together.
 */
const PROBE_MODEL = "Xenova/whisper-base.en";

let assetProbe: Promise<boolean> | null = null;

/**
 * Whether the self-hosted engine assets are actually served — the async half of
 * the capability gate.
 *
 * `LOCAL_WEB_STT_AVAILABLE` checks browser primitives, which is not the same
 * question: the deployed web host can carry the app without the ~105 MB of
 * Whisper/ORT assets (Cloudflare Pages rejects files over 25 MiB, so today it
 * always does). Without this check the mic was OFFERED and then died on first
 * tap when the model fetch 404'd. One small request against the first file the
 * worker will load answers it up front; any failure — 404, offline, misrouted
 * base URL — keeps voice hidden and text untouched.
 *
 * Probed once per session (the answer cannot change without a redeploy) and
 * shared: `cache: "no-store"` so a previously cached 404 cannot linger past the
 * deploy that fixes it.
 */
export function probeLocalSttAssets(): Promise<boolean> {
  if (!LOCAL_WEB_STT_AVAILABLE) return Promise.resolve(false);
  assetProbe ??= (async () => {
    const url = `${ASSET_BASE}/models/${PROBE_MODEL}/config.json`;
    try {
      const head = await fetch(url, { method: "HEAD", cache: "no-store" });
      if (head.ok) return true;
      // A static host that refuses HEAD is not a missing asset — ask again the
      // ordinary way. config.json is ~2 KB, still a cheap probe.
      if (head.status === 405 || head.status === 501) {
        const get = await fetch(url, { cache: "no-store" });
        return get.ok;
      }
      return false;
    } catch {
      return false;
    }
  })();
  return assetProbe;
}

function AudioCtx(): typeof AudioContext | null {
  return (
    (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ??
    null
  );
}

// ── Inference worker (singleton, kept warm across turns) ─────────────────────

let worker: Worker | null = null;
let workerReady: Promise<string> | null = null;

/**
 * Give the model this long to come up before we fail closed to text. First load
 * fetches the weights (~73 MB, same-origin) and compiles the WASM kernels, so the
 * ceiling is generous — it exists to catch a genuinely broken/missing model, not
 * a slow one. Without it, a load that never resolves hangs the mic on "getting
 * the mic ready…" forever (regression seen when a model wasn't provisioned).
 */
const WARM_TIMEOUT_MS = 30000;

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(WORKER_URL, { type: "module" });
  worker.addEventListener("message", (ev: MessageEvent) => {
    const m = (ev.data ?? {}) as { type?: string; message?: string; pct?: number };
    if ((m.type === "warn" || m.type === "error") && typeof console !== "undefined") {
      // console.info (not warn/error) so React's dev overlay doesn't augment it
      // with a giant component stack.
      console.info("[voice/stt-worker]", m.type, m.message);
    }
    if (m.type === "progress" && typeof m.pct === "number") {
      prepareProgressCb?.(m.pct);
    }
    // The download phase is over on either outcome; clear so the status line
    // moves on to "starting the voice engine".
    if (m.type === "ready" || (m.type === "error" && prepareProgressCb)) {
      prepareProgressCb?.(null);
    }
  });
  return worker;
}

/**
 * Model-download progress (0-99) while the worker warms, null otherwise.
 * One subscriber — the voice-input hook — so a bare callback slot, not an
 * emitter. Fires only on a browser's first visit; the model route serves
 * immutable+1y and later warms skip the download entirely.
 */
let prepareProgressCb: ((pct: number | null) => void) | null = null;

export function onLocalSttPrepareProgress(
  cb: ((pct: number | null) => void) | null,
): void {
  prepareProgressCb = cb;
}

/** Drop the worker + its cached warm promise so the next warm starts clean. */
function teardownWorker(): void {
  workerReady = null;
  worker?.terminate();
  worker = null;
}

/**
 * Warm the worker + model ahead of first use. Resolves with the chosen device.
 * FAILS CLOSED: rejects if the worker reports an init error (e.g. the model
 * 404s / won't load) or doesn't come up within WARM_TIMEOUT_MS, and tears the
 * worker down so the next attempt is fresh. A rejection surfaces as an STT error,
 * which falls back to text — never a permanent "getting the mic ready…" hang.
 */
function warmWorker(): Promise<string> {
  const w = ensureWorker();
  if (!workerReady) {
    workerReady = new Promise<string>((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        clearTimeout(timer);
        w.removeEventListener("message", onMsg);
      };
      const onMsg = (ev: MessageEvent) => {
        if (settled) return;
        const m = (ev.data ?? {}) as {
          type?: string;
          stage?: string;
          device?: string;
          message?: string;
        };
        if (m.type === "ready") {
          settled = true;
          cleanup();
          resolve(m.device ?? "wasm");
        } else if (m.type === "error" && m.stage === "init") {
          settled = true;
          cleanup();
          teardownWorker();
          reject(new Error(m.message ? String(m.message) : "stt_init_failed"));
        }
      };
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        teardownWorker();
        reject(new Error("stt_init_timeout"));
      }, WARM_TIMEOUT_MS);
      w.addEventListener("message", onMsg);
      w.postMessage({ type: "init" });
    });
  }
  return workerReady;
}

// ── Warm mic stream + audio graph ────────────────────────────────────────────

const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

let warmStream: MediaStream | null = null;
let warmStreamPromise: Promise<MediaStream> | null = null;
let audioCtx: AudioContext | null = null;
let workletLoaded = false;

function streamIsLive(s: MediaStream | null): s is MediaStream {
  return !!s && s.getAudioTracks().some((t) => t.readyState === "live");
}

async function getWarmStream(): Promise<MediaStream> {
  if (streamIsLive(warmStream)) return warmStream;
  if (!warmStreamPromise) {
    warmStreamPromise = navigator.mediaDevices
      .getUserMedia(MIC_CONSTRAINTS)
      .then((s) => {
        warmStream = s;
        return s;
      })
      .finally(() => {
        warmStreamPromise = null;
      });
  }
  return warmStreamPromise;
}

async function ensureAudioGraph(): Promise<AudioContext> {
  const Ctx = AudioCtx();
  if (!Ctx) throw new Error("no AudioContext");
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === "suspended") await audioCtx.resume().catch(() => {});
  if (!workletLoaded) {
    await audioCtx.audioWorklet.addModule(WORKLET_URL);
    workletLoaded = true;
  }
  return audioCtx;
}

/** Acquire the mic + start warming the model/graph ahead of the first turn. */
export async function prewarmLocalWebStt(): Promise<void> {
  if (!LOCAL_WEB_STT_AVAILABLE) return;
  try {
    await getWarmStream();
    // Fail-closed warm: swallow a rejection here (startLocalWebStt surfaces the
    // real error on use). Without the catch this would be an unhandled rejection.
    void warmWorker().catch(() => {});
    void ensureAudioGraph();
  } catch {
    /* ignore — startLocalWebStt surfaces the error on real use */
  }
}

/** Release mic + worker + audio graph when leaving voice mode / unmounting. */
export function releaseLocalWebStt(): void {
  warmStream?.getTracks().forEach((t) => t.stop());
  warmStream = null;
  worker?.terminate();
  worker = null;
  workerReady = null;
  void audioCtx?.close().catch(() => {});
  audioCtx = null;
  workletLoaded = false;
}

/**
 * Start one hands-free listening turn: stream mic PCM to the worker, and finalize
 * (onFinal + onEnd) when the worker detects end-of-utterance — or when stop() is
 * called. Fails closed. Reuses warm stream + worker + audio graph; onStart fires
 * on the first captured chunk.
 */
export function startLocalWebStt(handlers: SttHandlers): SttHandle | null {
  if (!LOCAL_WEB_STT_AVAILABLE) return null;

  let ended = false;
  let started = false;
  let source: MediaStreamAudioSourceNode | null = null;
  let node: AudioWorkletNode | null = null;
  let onWorkerMsg: ((ev: MessageEvent) => void) | null = null;

  const disconnectAudio = () => {
    try {
      source?.disconnect();
    } catch {
      /* ignore */
    }
    try {
      node?.disconnect();
    } catch {
      /* ignore */
    }
    if (node) node.port.onmessage = null;
    source = null;
    node = null;
  };

  const finish = () => {
    if (ended) return;
    ended = true;
    disconnectAudio();
    if (onWorkerMsg && worker) worker.removeEventListener("message", onWorkerMsg);
    onWorkerMsg = null;
    handlers.onEnd();
  };

  void (async () => {
    try {
      const stream = await getWarmStream();
      const w = ensureWorker();
      await warmWorker(); // ensure the model is loaded + 'ready'
      const ctx = await ensureAudioGraph();
      if (ended) return; // stop() raced ahead

      onWorkerMsg = (ev: MessageEvent) => {
        if (ended) return;
        const m = (ev.data ?? {}) as { type?: string; text?: string };
        // Batch: the worker emits a single 'final' per utterance (no partials).
        if (m.type === "final") {
          if (m.text) handlers.onFinal(m.text);
          finish();
        }
      };
      w.addEventListener("message", onWorkerMsg);
      w.postMessage({ type: "start", sampleRate: ctx.sampleRate });

      source = ctx.createMediaStreamSource(stream);
      node = new AudioWorkletNode(ctx, "opncue-capture");
      node.port.onmessage = (ev: MessageEvent) => {
        if (ended) return;
        const { pcm, rms } = ev.data as { pcm: Float32Array; rms: number };
        if (!started) {
          started = true;
          handlers.onStart?.();
        }
        handlers.onLevel?.(rms);
        w.postMessage({ type: "audio", pcm, rms }, [pcm.buffer]);
      };
      source.connect(node);
      node.connect(ctx.destination); // worklet outputs silence; keeps the graph pulling
    } catch (e) {
      handlers.onError(`local_stt_start_failed: ${String(e)}`);
      finish();
    }
  })();

  return {
    stop: () => {
      if (ended) return;
      disconnectAudio();
      worker?.postMessage({ type: "stop" }); // worker finalizes → emits 'final' → finish()
      setTimeout(() => finish(), 3000); // fallback if no 'final' arrives
    },
  };
}
